export interface Problem {
  readonly code: string;
  readonly status: number;
  readonly title: string;
  readonly detail: string;
  readonly request_id: string;
  readonly errors?: Readonly<Record<string, readonly string[]>>;
}

export class ApiProblem extends Error {
  public constructor(public readonly problem: Problem) {
    super(problem.detail);
    this.name = "ApiProblem";
  }
}

const apiOrigin = import.meta.env.VITE_API_ORIGIN ?? "http://localhost:8787";
const sessionKey = "baliktaniman.staff.session";
let accessToken = window.sessionStorage.getItem(sessionKey) ?? undefined;

export function setAccessToken(token: string | undefined): void {
  accessToken = token;
  if (token === undefined) window.sessionStorage.removeItem(sessionKey);
  else window.sessionStorage.setItem(sessionKey, token);
}

export interface LoginSession {
  readonly access_token: string;
  readonly token_type: "Bearer";
  readonly expires_at: string;
  readonly password_change_required: boolean;
}

export async function signIn(
  email: string,
  password: string,
): Promise<LoginSession> {
  const session = await api<LoginSession>("/auth/login", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });
  setAccessToken(session.access_token);
  return session;
}

export async function signOut(): Promise<void> {
  try {
    await api<undefined>("/auth/logout", { method: "POST" });
  } finally {
    setAccessToken(undefined);
  }
}

export async function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<void> {
  await api<undefined>("/me/password", {
    method: "POST",
    body: JSON.stringify({
      current_password: currentPassword,
      new_password: newPassword,
    }),
  });
}

export async function api<T>(
  path: string,
  options: RequestInit & { readonly idempotencyKey?: string } = {},
): Promise<T> {
  const headers = new Headers(options.headers);
  headers.set("Accept", "application/json");
  if (options.body !== undefined) {
    headers.set("Content-Type", "application/json");
  }
  if (options.idempotencyKey !== undefined) {
    headers.set("Idempotency-Key", options.idempotencyKey);
  }
  if (accessToken !== undefined) {
    headers.set("Authorization", `Bearer ${accessToken}`);
  }
  const response = await fetch(`${apiOrigin}/v1${path}`, {
    ...options,
    headers,
    credentials: "omit",
  });
  if (!response.ok) {
    const problem = (await response.json()) as Problem;
    throw new ApiProblem(problem);
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export interface DownloadedFile {
  readonly blob: Blob;
  readonly filename: string;
}

/** Fetch private binary evidence with the in-memory bearer session. */
export async function protectedBlob(
  path: string,
  accept = "application/octet-stream",
): Promise<Blob> {
  const headers = new Headers({ Accept: accept });
  if (accessToken !== undefined) {
    headers.set("Authorization", `Bearer ${accessToken}`);
  }
  const response = await fetch(`${apiOrigin}/v1${path}`, {
    headers,
    credentials: "omit",
  });
  if (!response.ok) {
    const problem = (await response.json()) as Problem;
    throw new ApiProblem(problem);
  }
  return response.blob();
}

function downloadFilename(disposition: string | null): string {
  const encoded = disposition?.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  const plain = disposition?.match(/filename="?([^";]+)"?/i)?.[1];
  let filename = "baliktaniman-export.csv";
  try {
    filename =
      encoded === undefined ? (plain ?? filename) : decodeURIComponent(encoded);
  } catch {
    filename = plain ?? filename;
  }
  return Array.from(filename)
    .map((character) =>
      character === "/" || character === "\\" || character.charCodeAt(0) < 32
        ? "_"
        : character,
    )
    .join("");
}

/** Download a protected export without putting the bearer session in a URL. */
export async function downloadFile(path: string): Promise<DownloadedFile> {
  const headers = new Headers({ Accept: "text/csv" });
  if (accessToken !== undefined) {
    headers.set("Authorization", `Bearer ${accessToken}`);
  }
  const response = await fetch(`${apiOrigin}/v1${path}`, {
    headers,
    credentials: "omit",
  });
  if (!response.ok) {
    const problem = (await response.json()) as Problem;
    throw new ApiProblem(problem);
  }
  return {
    blob: await response.blob(),
    filename: downloadFilename(response.headers.get("Content-Disposition")),
  };
}

export function mutationKey(prefix: string): string {
  return `${prefix}-${crypto.randomUUID()}`;
}

export function formatDate(value: string | null | undefined): string {
  if (value === null || value === undefined) return "Not available";
  return new Intl.DateTimeFormat("en-PH", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "Asia/Manila",
  }).format(new Date(value));
}
